// Presentation policy: which failures become a global notice (ApiNoticeHost) and which stay with their page.
//
// Global notice: network / offline / timeout / rate limit / unavailable / server / invalid answers of an action or a
// background request. Never a global notice: cancelled requests, 401 (the session flow owns it), 403 (responseGuard
// already shows one), validation, not-found and conflicts (the page shows them inline or as its own state).
// A page that renders the error itself passes { inline: true } and nothing is dispatched, so one failure is never shown twice.
// An older helper whose page has no inline error UI passes { always: true } for its action failures (upload, save):
// the user still gets one localized notice instead of a browser alert. 401 / 403 / cancelled are skipped even then.

import { notify } from "../notify";
import { errorText, normalizeApiError, normalizeThrown } from "./normalizeApiError";

const GLOBAL_KINDS = new Set(["network", "offline", "timeout", "rate_limited", "unavailable", "server", "invalid"]);
const NEVER_KINDS = new Set(["aborted", "unauthorized", "forbidden"]);

export const shouldNotifyGlobally = (error) => Boolean(error) && GLOBAL_KINDS.has(error.kind);

/**
 * Reports a failed API call. Returns the normalized error so the caller can also render it inline.
 * @param {unknown} errorOrNormalized
 * @param {{ inline?: boolean, always?: boolean, key?: string }} [options]
 */
export function reportApiError(errorOrNormalized, { inline = false, always = false, key } = {}) {
  const error = normalizeThrown(errorOrNormalized);
  const show = !inline && !NEVER_KINDS.has(error.kind) && (always || shouldNotifyGlobally(error));
  if (show) {
    notify(
      { en: errorText(error, "en"), ar: errorText(error, "ar") },
      {
        // One notice per code: a failing request that several components repeat does not stack.
        key: key || `api:${error.code}`,
        level: error.kind === "rate_limited" || error.kind === "offline" ? "warning" : "error",
        requestId: error.requestId,
      },
    );
  }
  return error;
}

// For older helpers that only kept the parsed body of a failed answer (the status was not kept): the body's code decides.
export const reportFailedBody = (data, options) => reportApiError(normalizeApiError({ status: 400, data }), { always: true, ...options });
